import { getToken } from "./identity";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new ApiError(res.status, data.error || `Request failed (${res.status})`);
  }
  return data as T;
}

export type FileResult = { kind: "blob"; blob: Blob } | { kind: "redirect"; url: string };

/**
 * Netlify Identity's JWT lives in localStorage, not a cookie, so a plain
 * `<a href>` to an API route would hit the function with no auth header at
 * all. Fetch with the same manual Authorization header the rest of this
 * client uses.
 *
 * documents-file.ts can't proxy files too large for a classic Function's
 * buffered response, so it answers those with a JSON `{ redirectUrl }`
 * instead of the binary — distinguish that from an actual PDF/DOCX by
 * Content-Type rather than assuming every 200 is the file itself.
 */
async function requestFile(path: string): Promise<FileResult> {
  const token = await getToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { headers });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    if (contentType.includes("application/json")) {
      try {
        const data = await res.json();
        if (data.error) message = data.error;
      } catch {
        // response wasn't valid JSON despite the header — keep the generic message
      }
    }
    throw new ApiError(res.status, message);
  }
  if (contentType.includes("application/json")) {
    const data = await res.json();
    return { kind: "redirect", url: data.redirectUrl };
  }
  return { kind: "blob", blob: await res.blob() };
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  getFile: requestFile,
};
